import {
  type CommsTurn,
  isCancelled,
  type ReplyClass,
  type ReplyVerdict,
  isUnpublished,
  publicationState,
  publishable,
  type PublicationState,
  refusalText,
  shiftStanding,
  standingWordsOf,
} from '@reditelji/shared';
import type { EventDay, Shift, ShiftAnswerView } from '../api/types';
import type { VolunteerRow } from '../api/volunteers';
import type { PostRow } from '../api/posts';
import type { CoordinatorLabelOf } from '../lib/people';
import { plDni } from '../lib/ui';


export interface VolunteerShift extends Shift {
  dayIndex: number;
  dayLabel: string;
  postCode: string;
  postMicro: string;
  clash: boolean;
}

export interface VolunteerRollup {
  shifts: VolunteerShift[];
  minutes: number;
  clash: boolean;
  /**
   * How far behind the plan this person's briefing page is (SPEC §1.1F).
   * `null` for a team's place, which has no briefing at all.
   */
  publication: PublicationState | null;
  /** Briefing missing or sent for an older schedule version. */
  briefStale: boolean;
  /** Our question has gone unanswered past the threshold (`commsTurn`). */
  noReply: boolean;
  /** Whole days the conversation has waited — on them or on us. */
  waitDays: number;
  /** They wrote, and nobody has answered yet. */
  awaitingUs: boolean;
}

/**
 * What the label on a thread message says: its own verdict, or — on the word
 * the conversation hangs on, when nobody waits on it although it asks — why
 * not, as the API read it with the thread (`readTurn`). Only the deciding word
 * automatic replies and a message a coordinator labelled carry one.
 */
export type ReplyChipState =
  | { kind: 'auto' }
  | { kind: 'verdict'; cls: ReplyClass; by: ReplyVerdict['by'] }
  | { kind: 'settled'; text: string };

export function replyChipState(
  m: { id: string; direction: 'in' | 'out' | 'note'; reply?: ReplyVerdict | null; replyOverride?: ReplyClass | null },
  deciding: { messageId: string; settled: 'answered' | 'not-chased' | null } | null | undefined,
): ReplyChipState | null {
  const verdict = m.reply;
  if (!verdict || m.direction === 'note') return null;
  if (verdict.cls === 'auto') return { kind: 'auto' };
  // A coordinator's word stays in sight wherever it is, so it can be taken back.
  if (deciding?.messageId !== m.id) return m.replyOverride ? { kind: 'verdict', cls: verdict.cls, by: verdict.by } : null;
  if (deciding.settled === 'not-chased') return { kind: 'settled', text: 'ne pride · ne čakamo' };
  if (deciding.settled === 'answered') {
    return { kind: 'settled', text: m.direction === 'in' ? 'odgovorjeno z vnosom' : 'odgovoril brez pošte' };
  }
  return { kind: 'verdict', cls: verdict.cls, by: verdict.by };
}


export const daysSince = (iso: string | null): number =>
  iso === null ? 0 : Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000));

/**
 * What a rollup actually needs.
 *
 * Narrower than `VolunteerRow` so an external team's place can be rolled up too
 * (SPEC §1.1J): the list endpoint returns those without the roster's derived
 * comms and briefing fields, and a type claiming otherwise would have this
 * compute a briefing state for something that has no briefing.
 */
export interface RollupSubject {
  id: string;
  scheduleVersion: number;
  briefingVersion?: number | null;
  /** Whose turn their conversation is in; absent where it is not shown (a team, an organizer). */
  commsTurn?: CommsTurn | null;
}

export function rollupFor(
  v: RollupSubject,
  allShifts: Shift[],
  days: EventDay[],
  postById: Map<string, PostRow>,
): VolunteerRollup {
  const dayIndex = new Map(days.map((d, i) => [d.id, i]));
  const own = allShifts
    // A post is only ever soft-deleted, and its shifts are left where they are,
    // so `/shifts` still hands them over while `/posts` no longer lists the post
    // they belong to. Counting them would put hours in somebody's total for a
    // place that no longer exists, under a row reading "?".
    .filter(
      (s) => s.volunteerId === v.id && s.state !== 'cancelled' && postById.has(s.postId),
    )
    .sort((a, b) =>
      a.eventDayId === b.eventDayId
        ? a.fromMin - b.fromMin
        : (dayIndex.get(a.eventDayId) ?? 0) - (dayIndex.get(b.eventDayId) ?? 0),
    );
  const clashIds = new Set<string>();
  for (let i = 0; i < own.length; i += 1) {
    for (let j = i + 1; j < own.length; j += 1) {
      const a = own[i]!;
      const b = own[j]!;
      if (a.eventDayId === b.eventDayId && a.fromMin < b.toMin && b.fromMin < a.toMin) {
        clashIds.add(a.id);
        clashIds.add(b.id);
      }
    }
  }
  const shifts: VolunteerShift[] = own.map((s) => {
    const p = postById.get(s.postId);
    return {
      ...s,
      dayIndex: dayIndex.get(s.eventDayId) ?? 0,
      dayLabel: days.find((d) => d.id === s.eventDayId)?.label ?? '',
      postCode: p?.code ?? '?',
      postMicro: p?.micro ?? '',
      clash: clashIds.has(s.id),
    };
  });
  return {
    shifts,
    minutes: own.reduce((a, s) => a + (s.toMin - s.fromMin), 0),
    clash: clashIds.size > 0,
    // `undefined` means the subject has no briefing concept at all (a team's
    // place), which is not the same as one that was never sent.
    publication:
      v.briefingVersion === undefined
        ? null
        : publicationState({
            scheduleVersion: v.scheduleVersion,
            briefingSentVersion: v.briefingVersion,
            hasShifts: own.some(publishable),
          }),
    briefStale:
      v.briefingVersion !== undefined &&
      isUnpublished(
        publicationState({
          scheduleVersion: v.scheduleVersion,
          briefingSentVersion: v.briefingVersion,
          hasShifts: own.some(publishable),
        }),
      ),
    noReply: v.commsTurn?.noReply ?? false,
    waitDays: v.commsTurn?.days ?? 0,
    awaitingUs: v.commsTurn?.whose === 'us',
  };
}

/**
 * Where a person's shifts stand, shift by shift (SPEC §1.1F) — the board's
 * volunteer and the roster's row both carry it, for every login. The words of
 * an answer are `shiftAnswers`, which a login without `comms:read` is not given.
 */
export interface AnswerSubject {
  shiftStatus?: Record<string, ShiftAnswerView['status']>;
  shiftAnswers?: Record<string, ShiftAnswerView>;
}

/**
 * The word standing on each shift. `shiftStatus` says it; a payload without it
 * — the API a deploy is replacing — still says it inside `shiftAnswers`, and
 * reading that keeps "Potrdi preostale" from overruling a refusal meanwhile.
 */
export function standingWords(person: AnswerSubject): Record<string, ShiftAnswerView['status']> | undefined {
  if (person.shiftStatus) return person.shiftStatus;
  return person.shiftAnswers ? standingWordsOf(person.shiftAnswers) : undefined;
}

/** Refused or asked to change — `shiftStanding`'s reading, the one the board colours by. */
export const refuses = (status: ShiftAnswerView['status'] | undefined): status is 'cant' | 'change' =>
  shiftStanding(status) === 'zavrnjen';

/**
 * The shifts, on any day, whose standing answer turns them down or asks for a
 * change. Only an assigned shift is asked anything: a proposal was never on a
 * briefing and a finished shift has nothing left to answer. `skipShiftId`
 * leaves out the shift being edited, whose own answer is not a reason to
 * hesitate over it.
 */
export function answeredRefusals(
  person: AnswerSubject,
  rollup: VolunteerRollup | undefined,
  skipShiftId?: string,
): VolunteerShift[] {
  const answers = standingWords(person);
  if (!answers || !rollup) return [];
  return rollup.shifts.filter((s) => s.id !== skipShiftId && s.state === 'assigned' && refuses(answers[s.id]));
}

/**
 * The other half of a shift split at midnight: the same post, ending at 24:00
 * on one event day and starting at 00:00 on the next (IMPORT-2026.md) — the
 * rollup's reading of `splitPartner`, which pairs them by date.
 */
function splitHalf(shifts: VolunteerShift[], s: VolunteerShift): VolunteerShift | undefined {
  if (s.toMin === 1440) {
    return shifts.find((p) => p.postId === s.postId && p.dayIndex === s.dayIndex + 1 && p.fromMin === 0);
  }
  if (s.fromMin === 0) {
    return shifts.find((p) => p.postId === s.postId && p.dayIndex === s.dayIndex - 1 && p.toMin === 1440);
  }
  return undefined;
}

/**
 * The assigned shifts nobody has answered yet — what "Potrdi preostale"
 * confirms. A half of a midnight split whose other half stands refused is left
 * out: the server answers both halves at once, so agreeing to this one would
 * overwrite that refusal.
 */
export function unansweredShifts(person: AnswerSubject, rollup: VolunteerRollup | undefined): VolunteerShift[] {
  const answers = standingWords(person);
  if (!answers || !rollup) return [];
  return rollup.shifts.filter((s) => {
    if (s.state !== 'assigned' || answers[s.id] !== undefined) return false;
    const half = splitHalf(rollup.shifts, s);
    return !(half && refuses(answers[half.id]));
  });
}

/** The assigned shifts their holder agreed to — only these make a schedule read as confirmed. */
export function confirmedShifts(person: AnswerSubject, rollup: VolunteerRollup | undefined): VolunteerShift[] {
  const answers = standingWords(person);
  if (!answers || !rollup) return [];
  return rollup.shifts.filter((s) => s.state === 'assigned' && shiftStanding(answers[s.id]) === 'potrjen');
}

/** "ne more: GM3 · Pet 11:45–16:30" — a refused shift named the way a coordinator finds it. */
export function refusalLabel(person: AnswerSubject, s: VolunteerShift): string {
  return refusalText({ status: standingWords(person)?.[s.id] ?? 'cant', postCode: s.postCode, dayLabel: s.dayLabel, fromMin: s.fromMin, toMin: s.toMin });
}

/** '4 h 30 min' from minutes. */
export function hoursLabel(min: number): string {
  const h = Math.floor(min / 60);
  return min % 60 ? `${h} h ${min % 60} min` : `${h} h`;
}

export type SchedStateKind = 'potrjen' | 'caka' | 'zavrnjen' | 'none' | 'changed' | 'nepride';

export interface SchedState {
  kind: SchedStateKind;
  label: string;
  meta: string;
  bg: string;
  bd: string;
  fg: string;
  /** Show the "Označi kot potrjeno" action. */
  pending: boolean;
}

const SCHED_TONES: Record<SchedStateKind, [string, string, string, string]> = {
  potrjen: ['Razpored potrjen', 'oklch(0.955 0.035 155)', 'oklch(0.85 0.055 155)', 'oklch(0.33 0.06 155)'],
  caka: ['Čaka potrditev', 'oklch(0.975 0.03 75)', 'oklch(0.87 0.06 75)', 'oklch(0.38 0.07 75)'],
  zavrnjen: ['Razpored zavrnjen', 'oklch(0.965 0.04 25)', 'oklch(0.85 0.07 25)', 'oklch(0.42 0.11 25)'],
  changed: ['Sprememba čaka uskladitev', 'oklch(0.97 0.03 285)', 'oklch(0.87 0.06 285)', 'oklch(0.4 0.1 285)'],
  none: ['Brez izmen', 'oklch(0.97 0.005 95)', 'oklch(0.92 0.006 95)', 'oklch(0.5 0.012 90)'],
  // Solid, where "Razpored zavrnjen" is a tint: a refusal can still be talked
  // round, somebody who is not coming is settled (SPEC §1.1K).
  nepride: ['Ne pride', 'oklch(0.55 0.14 25)', 'oklch(0.5 0.14 25)', 'oklch(1 0 0)'],
};

/** The pill's words for a state — what a filter over these states is labelled with. */
export const schedStateLabel = (kind: SchedStateKind): string => SCHED_TONES[kind][0];

const dateLabel = (iso: string | null | undefined) =>
  !iso
    ? ''
    : new Date(iso).toLocaleDateString('sl-SI', {
        day: 'numeric',
        month: 'numeric',
        timeZone: 'Europe/Ljubljana',
      });

/** Whole-schedule confirmation pill (per person, per version — SPEC §1.1F). */
/**
 * `labelOf` names the coordinator behind an answer they recorded — "uveljavil
 * Nina Kranjc" — and is left out where nobody reads who it was.
 */
export function schedStateFor(v: VolunteerRow, r: VolunteerRollup, labelOf?: CoordinatorLabelOf): SchedState {
  // Ahead of every answer they gave: once they are not coming, none of it is
  // pending any more (SPEC §1.1K).
  if (isCancelled(v)) {
    const [label, bg, bd, fg] = SCHED_TONES.nepride;
    return { kind: 'nepride', label, meta: `zabeleženo ${dateLabel(v.cancelledAt)}`, bg, bd, fg, pending: false };
  }
  const changed =
    v.confirmationStatus === 'čaka' && v.confirmedVersion > 0 && v.confirmedVersion < v.scheduleVersion;
  const kind: SchedStateKind =
    r.shifts.length === 0 && v.confirmationStatus === 'čaka'
      ? 'none'
      : changed
        ? 'changed'
        : v.confirmationStatus === 'potrjen'
          ? 'potrjen'
          : v.confirmationStatus === 'zavrnjen'
            ? 'zavrnjen'
            : 'caka';
  const [label, bg, bd, fg] = SCHED_TONES[kind];
  const byCoordinator =
    labelOf && v.confirmationActorType === 'coordinator' ? labelOf(v.confirmationActorId) : null;
  // When it came is the answer's; a login not shown it gets the word alone (SPEC §1.1L).
  const when = v.confirmationAt ? ` ${dateLabel(v.confirmationAt)}` : '';
  const meta =
    kind === 'changed'
      ? 'razpored se je spremenil po potrditvi'
      : kind === 'potrjen'
        ? `potrjeno${when}${byCoordinator ? ` · uveljavil ${byCoordinator}` : ''}`
        : kind === 'zavrnjen'
          ? `zavrnjeno${when}${byCoordinator ? ` · zabeležil ${byCoordinator}` : ''}`
          : kind === 'caka'
            ? v.briefingSentAt === undefined
              ? // Not told what was sent (SPEC §1.1L) — only that an answer is owed.
                'čaka potrditev'
              : v.briefingSentAt
              ? r.noReply
                ? `brez odgovora ${plDni(r.waitDays)}`
                : `predlog ${v.briefingMailedAt ? 'poslan' : 'objavljen'} ${dateLabel(v.briefingSentAt)}`
              : 'predlog še ni poslan'
            : 'brez razporejenih izmen';
  return { kind, label, meta, bg, bd, fg, pending: kind === 'caka' || kind === 'zavrnjen' || kind === 'changed' };
}

export type StepTone = 'done' | 'now' | 'bad' | 'wait' | 'todo';

export interface LifeStep {
  title: string;
  meta: string;
  tone: StepTone;
  act?: string;
  run?: () => void;
  /** A second action beside `act`. */
  alt?: { label: string; run: () => void };
}

export const STEP_TONES: Record<
  StepTone,
  { dot: string; mark: string; fg: string; bg: string; bd: string }
> = {
  done: { dot: 'oklch(0.62 0.09 155)', mark: '✓', fg: 'oklch(0.32 0.05 155)', bg: 'oklch(1 0 0)', bd: 'oklch(0.93 0.006 95)' },
  now: { dot: 'oklch(0.72 0.11 75)', mark: '●', fg: 'oklch(0.4 0.08 75)', bg: 'oklch(0.99 0.018 75)', bd: 'oklch(0.9 0.05 75)' },
  bad: { dot: 'oklch(0.58 0.15 25)', mark: '✕', fg: 'oklch(0.44 0.12 25)', bg: 'oklch(0.985 0.02 25)', bd: 'oklch(0.9 0.05 25)' },
  wait: { dot: 'oklch(0.6 0.11 285)', mark: '↻', fg: 'oklch(0.4 0.1 285)', bg: 'oklch(0.985 0.018 285)', bd: 'oklch(0.9 0.045 285)' },
  todo: { dot: 'oklch(0.85 0.008 95)', mark: '', fg: 'oklch(0.6 0.012 90)', bg: 'oklch(1 0 0)', bd: 'oklch(0.94 0.005 95)' },
};

export interface LifeActions {
  findGap: () => void;
  openComms: () => void;
  sendBriefing: () => void;
  /** Publish without mail, for somebody agreed with by phone (SPEC §1.1F). */
  publish: () => void;
  confirm: () => void;
  /** Open one of their shifts on Časovnica. */
  openShift: (s: VolunteerShift) => void;
}

/**
 * "Pot do razporeditve" — the assignment lifecycle: the briefing
 * email is both the proposal and the instructions, so "predlog poslan" = first
 * publication and "navodila aktualna" = a publication of the current version.
 * Publication is not always a mail (SPEC §1.1F), so the wording follows
 * `briefingMailedAt` while the steps themselves follow `briefingSentAt`.
 */
export function lifeSteps(
  v: VolunteerRow,
  r: VolunteerRollup,
  sched: SchedState,
  act: LifeActions,
): LifeStep[] {
  // The Ne pride card stands in for the whole path (SPEC §1.1K).
  if (isCancelled(v)) return [];
  const nS = r.shifts.length;
  // Published, by whatever route — that is what the volunteer can see. Whether
  // it went by e-mail is a separate question the wording has to keep straight:
  // "Objavi po dogovoru" and a handed-over link publish without writing.
  // `!= null`: a login without comms:read gets neither field, and absent is not sent.
  const sent = v.briefingSentAt != null;
  const mailed = v.briefingMailedAt != null;
  const briefCurrent = v.briefingVersion === v.scheduleVersion;
  const changed = sched.kind === 'changed';
  const steps: LifeStep[] = [];
  steps.push(
    nS
      ? { title: 'Predlog razporeditve', tone: 'done', meta: `${plIzmen(nS)} · ${hoursLabel(r.minutes)}` }
      : {
          title: 'Predlog razporeditve',
          tone: 'now',
          meta: 'oseba še ni razporejena na nobeno mesto',
          act: 'Poišči vrzel',
          run: act.findGap,
        },
  );
  steps.push(
    sent
      ? {
          title: 'Predlog poslan prostovoljcu',
          tone: 'done',
          meta: mailed ? 'brifing poslan po e-pošti' : 'razpored objavljen po dogovoru',
        }
      : {
          title: 'Predlog poslan prostovoljcu',
          tone: nS ? 'now' : 'todo',
          meta: nS
            ? v.email
              ? 'predlog čaka na pošiljanje'
              : 'oseba nima e-naslova — dogovori se po telefonu in objavi razpored'
            : 'najprej razporedi izmene',
          // Without an e-naslov no template can reach them, so publishing by
          // hand is the only way their page ever fills in (SPEC §1.1F).
          act: nS ? (v.email ? 'Pošlji navodila' : 'Objavi po dogovoru') : undefined,
          run: v.email ? act.sendBriefing : act.publish,
        },
  );
  // What they turned down, shift by shift, where the payload says (SPEC §1.1F).
  const refused = answeredRefusals(v, r);
  const firstRefused = refused[0];
  steps.push(
    sched.kind === 'zavrnjen'
      ? {
          title: 'Odziv prostovoljca',
          tone: 'bad',
          meta: firstRefused
            ? `${refusalLabel(v, firstRefused)}${refused.length > 1 ? ` · še ${refused.length - 1}` : ''}`
            : v.confirmationComment === undefined
              ? // The words are correspondence (SPEC §1.1L); the fact is not.
                'razpored zavrnjen'
              : `zavrnitev: ${(v.confirmationComment || 'brez pojasnila').slice(0, 90)}`,
          act: 'Odpri pogovor',
          run: act.openComms,
          alt: firstRefused ? { label: 'Odpri izmeno', run: () => act.openShift(firstRefused) } : undefined,
        }
      : sched.kind === 'potrjen' || changed
        ? { title: 'Odziv prostovoljca', tone: 'done', meta: sched.kind === 'potrjen' ? sched.meta : 'potrjena prejšnja različica' }
        : {
            title: 'Odziv prostovoljca',
            tone: sent ? 'now' : 'todo',
            meta: sent
              ? r.noReply
                ? `brez odgovora ${plDni(r.waitDays)}`
                : 'čaka na odgovor'
              : 'čaka na poslan predlog',
            act: sent ? 'Odpri pogovor' : undefined,
            run: act.openComms,
          },
  );
  if (changed) {
    steps.push({
      title: 'Usklajevanje spremembe',
      tone: 'wait',
      meta: 'razpored se je spremenil po potrditvi — potrebna je nova potrditev',
      act: 'Označi kot potrjeno',
      run: act.confirm,
    });
  }
  steps.push(
    briefCurrent && sent
      ? {
          title: 'Navodila za aktualni razpored',
          tone: 'done',
          meta: mailed
            ? 'prostovoljec ima navodila za svoje mesto'
            : 'razpored je objavljen — navodila si ogleda na svoji povezavi',
        }
      : {
          title: 'Navodila za aktualni razpored',
          tone: sched.kind === 'potrjen' || changed ? 'now' : 'todo',
          meta:
            sched.kind === 'potrjen' || changed
              ? 'razpored se je od zadnjega pošiljanja spremenil — pošlji navodila'
              : 'po potrjenem razporedu',
          act: nS ? (v.email ? 'Pošlji navodila' : 'Objavi po dogovoru') : undefined,
          run: v.email ? act.sendBriefing : act.publish,
        },
  );
  const doneAll = nS > 0 && sent && sched.kind === 'potrjen' && briefCurrent;
  steps.push(
    doneAll
      ? { title: 'Razpored in navodila urejeni', tone: 'done', meta: 'vse urejeno — nič ne čaka nate' }
      : { title: 'Razpored in navodila urejeni', tone: 'todo', meta: 'zaključi korake zgoraj' },
  );
  return steps;
}

/** 'N izmen' with Slovenian plural. */
export function plIzmen(n: number): string {
  const r = n % 100;
  return `${n} ${r === 1 ? 'izmena' : r === 2 ? 'izmeni' : r === 3 || r === 4 ? 'izmene' : 'izmen'}`;
}

export interface Issue {
  text: string;
}

/** "Zadeve za ureditev" card. */
export function issuesFor(v: VolunteerRow, r: VolunteerRollup, sched: SchedState): Issue[] {
  // Nothing left to arrange with somebody who is not coming (SPEC §1.1K).
  if (isCancelled(v)) return [];
  const out: Issue[] = [];
  if (r.clash) out.push({ text: 'Prekrivajoče izmene — oseba je hkrati na dveh mestih.' });
  if (r.shifts.length > 0 && !v.email)
    out.push({ text: 'Nima e-naslova — navodil ni mogoče poslati po e-pošti.' });
  if (r.shifts.length > 0 && v.briefingSentAt === null)
    out.push({ text: 'Predlog razporeda še ni poslan, čeprav ima dodeljene izmene.' });
  else if (sched.kind === 'potrjen' && r.briefStale)
    out.push({ text: 'Navodila niso poslana za aktualno različico razporeda.' });
  if (r.noReply)
    out.push({ text: `Na naše sporočilo ni odgovora že ${plDni(r.waitDays)} — razmisli o klicu.` });
  return out;
}

/**
 * Whether the stored summary was written over a thread other than the one on
 * screen. The API writes down how many messages it read, and the thread is
 * the same rows, so a different count means something arrived, was sent or was
 * removed since — and its warning may no longer be true. Nothing is behind
 * until both are known.
 */
export function summaryBehind(summary: { messageCount: number } | null, messageIds: string[] | null): boolean {
  return summary !== null && messageIds !== null && summary.messageCount !== messageIds.length;
}
